import React, { FC, Fragment, useMemo, useContext, useRef, useState, useEffect } from "react";

import InfoIcon from '@pixel/assets/icons/Info'
import HelpIcon from '@pixel/assets/icons/Help'

import AutoSelect from '@pixel/core/AutoSelect';
import Checkbox from '@pixel/core/Checkbox';
import CircularProgress from '@pixel/core/CircularProgress';
import ContentModal from "@pixel/core/ContentModal";
import Divider from '@pixel/core/Divider';
import FormInputLayout from '@pixel/core/FormInputLayout';
import FormGroup from '@pixel/core/FormGroup';
import FormControlLabel from '@pixel/core/FormControlLabel';
import TextField from '@pixel/core/TextField';
import ToolTip from '@pixel/core/Tooltip';
import Radio from '@pixel/core/Radio';
import RadioGroup from '@pixel/core/RadioGroup';
import Stack from '@pixel/core/Stack';
import { useSnackbar } from '@pixel/core/SnackbarService'

import HTMLEditor from '@pixel/internal/Editor/HTMLEditor';

import Typography from "@mui/material/Typography";
import UserEmailTemplatesInput from "../UserEmailTemplatesInput";
import CandidateListContext from "../../contexts/candidate/candidateListContext";
import useRejectCandidates, { RejectionDisposition, useRejectCandidateReq } from "../../hooks/useRejectCandidates";
import { useLocaleType, useMultipleLocalizedText } from "../../contexts/Localization/localizationHooks";
import formatPluralString from '../../utils/formatPluralString'
import convertLineBreaksToBR, { removeLineBreaks } from "../../utils/formatEmailBody";
import FloatingCircularProgress from "./FloatingCircularProgress";

type TemplateTypes = 'company' | 'personal'

interface ICallback {
  (): void;
}

interface ActionModalProps {
  open: boolean;
  onClose: ICallback;
  destinationJobId?: string;
}

const DEFAULT_DAYS_AFTER = 3
const MINIMUM_DAYS_AFTER = 1
const MAXIMUM_DAYS_AFTER = 15

const RejectCandidateModal: FC<ActionModalProps> = ({
  open = false,
  onClose = () => { },
  destinationJobId,
}) => {
  const snack = useSnackbar()
  const userLocalePreference = useLocaleType()
  const {
    AGENCY_COPIED,
    COMMON_AGENCY,
    COMMON_AND,
    COMMON_FROM,
    COMMON_TO,
    COMMON_TEMPLATE,
    COMMON_DISPOSITION,
    COMMON_CANDIDATE,
    COMMON_CANDIDATES,
    COMMON_SUBJECT,
    HIRE_CANCEL,
    REJECT_CANDIDATE,
    REJECT_CANDIDATES,
    COMMON_SUBJECT_IS_REQUIRED,
    JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD,
    JV_APPLICANT_CANDIDATES_HAVE_BEEN_REJECTED,
    JV_APPLICANT_CANDIDATES_HAVE_BEEN_REJECTED_AND_MOVED,
    JV_SENDREJECTION_CONTROLLER_MESSAGE_SENT_ERROR,
    CANDIDATE_REJECT_APPLICATIONS,
    CANDIDATE_SEND_REJECTION_EMAIL,
    SEND_ANONYMOUSLY,
    CANDIDATES_REJECT_LATER,
    COMMON_DAYS,
    PERSONAL_EMAIL_TEMPLATES_COMPANY_TEMPLATES,
    PERSONAL_EMAIL_TEMPLATES_MY_TEMPLATES,
    CANDIDATES_MISSING_EMAIL_1,
    CANDIDATES_MISSING_EMAIL_MANY,
    AGENCY_CANDIDATES_MISSING_EMAIL_1,
    AGENCY_CANDIDATES_MISSING_EMAIL_MANY,
    CANDIDATES_REJECT_TOOLTIP,
    I18N_CANDIDATES_AGENCY_COMMON,
    APPLICANT_CANDIDATENAVIGATION_CANDIDATE_S_,
    I18N_CANDIDATE_BTN_MOVE_REJECT,
  } = useMultipleLocalizedText()

  const {
    validatedCandidates,
    validatedCandidateIds,
  } = useValidatedCandidatesList()

  const rejectCandidatesProps = useMemo(() => ({
    applications: validatedCandidateIds,
  }), [validatedCandidates])

  const rejectCandidatesData = useRejectCandidates(rejectCandidatesProps)

  const htmlEditorRef = useRef(null);
  const [disposition, setDisposition] = useState<RejectionDisposition>(null)
  const [dispositionError, setDispositionError] = useState('')
  const [sendRejectionEmail, setSendRejectionEmail] = useState(!destinationJobId)
  const [rejectFromAll, setRejectFromAll] = useState(false)
  const [delayRejection, setDelayRejection] = useState(false)
  const [daysAfter, setDaysAfter] = useState(DEFAULT_DAYS_AFTER)
  const [daysAfterError, setDaysAfterError] = useState('')
  const [templateType, setTemplateType] = useState<TemplateTypes>('company')
  const [templateChosen, setTemplateChosen] = useState(null)
  const [subjectError, setSubjectError] = useState('')
  const [subject, setSubject] = useState('')
  const [bodyError, setBodyError] = useState('')
  const templateRef = useRef(null)
  templateRef.current = templateChosen


  const handleHTMLEditorLoad = () => {
    setTimeout(() => {
      if (htmlEditorRef.current && templateRef.current) {
        htmlEditorRef.current.setValue(convertLineBreaksToBR(templateRef.current.body))
      }
    }, 10)
  }

  const nonAgencyApplications = useMemo(() => validatedCandidates.filter(candidate => !candidate.agencyApp), [validatedCandidates])
  const agencyApplications = useMemo(() => validatedCandidates.filter(candidate => candidate.agencyApp), [validatedCandidates])

  const validNonAgencyApplications = useMemo(() => nonAgencyApplications.filter(candidate => Boolean(candidate.candidate.email)), [nonAgencyApplications])
  const nonValidNonAgencyApplications = useMemo(() => nonAgencyApplications.filter(candidate => !Boolean(candidate.candidate.email)), [nonAgencyApplications])

  const validAgencyApplications = useMemo(() => agencyApplications.filter(candidate => Boolean(candidate.candidate.email)), [agencyApplications])
  const nonValidAgencyApplications = useMemo(() => agencyApplications.filter(candidate => !Boolean(candidate.candidate.email)), [agencyApplications])

  const nonAgencyValidNamesList = useMemo(() => {
    if (nonAgencyApplications.length > 0) {
      return nonAgencyApplications.map(candidate => <Fragment key={candidate.EId}>{candidate.candidate.fullName} <br /></Fragment>)
    }

    return ''
  }, [nonAgencyApplications])

  const agencyValidNamesList = useMemo(() => {
    if (agencyApplications.length > 0) {
      return agencyApplications.map(candidate => <Fragment key={candidate.EId}>{candidate.candidate.fullName} <br /></Fragment>)
    }

    return ''
  }, [agencyApplications])

  const toText = useMemo(() => getToText(validNonAgencyApplications, validAgencyApplications, {
    agencyCopied: AGENCY_COPIED,
    agencyCandidates: I18N_CANDIDATES_AGENCY_COMMON,
    and: COMMON_AND,
    candidates: APPLICANT_CANDIDATENAVIGATION_CANDIDATE_S_,
  }), [nonAgencyApplications, agencyApplications])

  useEffect(() => {
    setTemplateChosen(null);
    if (rejectCandidatesData.templates.length > 0) {
      if (templateType === 'company') {
        const userLocaleTemplates = rejectCandidatesData?.templates?.filter(template => template.locale.toLowerCase() === userLocalePreference.toLowerCase()) || []
        const defaultTemplate = userLocaleTemplates.find(template => template.isDefault)
        if (defaultTemplate) {
          setTemplateChosen(defaultTemplate)
        }
      }
    }
  }, [rejectCandidatesData.templates, templateType])

  useEffect(() => {
    if (sendRejectionEmail && templateChosen && (templateChosen.EId || templateChosen.SId)) {
      setSubject(templateChosen.subject)
      if (htmlEditorRef?.current) {
        htmlEditorRef.current.setValue(convertLineBreaksToBR(templateChosen.body))
      }
    } else {
      setSubject('');
      if (htmlEditorRef?.current) {
        htmlEditorRef.current.setValue('');
      }
    }
  }, [sendRejectionEmail, templateChosen])

  const {
    loading: sendLoading,
    error,
    response,
    rejectCandidatePost,
  } = useRejectCandidateReq()

  const handleReject = () => {
    console.log("Cancel Modal");
  };
  const handleResolve = () => {
    let hasErrors = false

    setDispositionError('')
    setDaysAfterError('')
    setSubjectError('')
    setBodyError('')

    if (!disposition) {
      hasErrors = true
      setDispositionError(JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD)
    }

    // extra validation if sendRejectionEmail is true
    if (sendRejectionEmail) {
      if (!subject) {
        hasErrors = true
        setSubjectError(COMMON_SUBJECT_IS_REQUIRED)
      }
      if (htmlEditorRef.current.getValue() === '') {
        hasErrors = true
        setBodyError(JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD)
      }
      if (rejectCandidatesData.isRejectLaterEnabled && !daysAfter) {
        hasErrors = true
        setDaysAfterError(JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD)
      }
    }

    if (hasErrors) {
      return true
    }

    rejectCandidatePost({
      bulk: true,
      dispositionId: disposition.EId,
      sendRejectionEmail,
      rejectFromAllApplications: rejectFromAll,
      moveCandidateEnabled: !destinationJobId,
      destinationJobId: destinationJobId,
      messageDataItem: {
        entityIdList: validatedCandidateIds,
        subject,
        content: removeLineBreaks(htmlEditorRef.current ? htmlEditorRef.current.getValue() : ''),
        scheduleLaterNotification: delayRejection,
        scheduleLaterNotificationDays: delayRejection ? daysAfter : DEFAULT_DAYS_AFTER,
        locale: (templateChosen && templateChosen.locale) ? templateChosen.locale : undefined,
      }
    })
      .then((response: any) => {
        if (response.msgInfo === 200) {
          snack.showSuccess(destinationJobId ? JV_APPLICANT_CANDIDATES_HAVE_BEEN_REJECTED_AND_MOVED : JV_APPLICANT_CANDIDATES_HAVE_BEEN_REJECTED)
          onClose()
        } else {
          console.log(response)
          snack.showError(JV_SENDREJECTION_CONTROLLER_MESSAGE_SENT_ERROR)
        }
      })
      .catch(err => {
        console.log(err)
        snack.showError(JV_SENDREJECTION_CONTROLLER_MESSAGE_SENT_ERROR)
      })

    return true
  };

  const daysAfterDate = useMemo(() => {
    const currentDate = new Date()
    currentDate.setDate(currentDate.getDate() + (daysAfter || DEFAULT_DAYS_AFTER))
    return new Date(currentDate)
  }, [daysAfter])

  const formattedDate = useMemo(() =>
    new Intl.DateTimeFormat("en-US", {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(daysAfterDate)
  , [daysAfterDate])

  return (
    <ContentModal
      id="reject-candidate"
      open={open}
      onClose={onClose}
      showClose={true}
      sizeVariant="md"
      title={REJECT_CANDIDATES}
      labelCloseAria="close"
      rejectActions={[
        {
          reason: "cancel",
          label: HIRE_CANCEL,
        },
      ]}
      resolveActions={[
        {
          reason: "ok",
          label: destinationJobId ? I18N_CANDIDATE_BTN_MOVE_REJECT : REJECT_CANDIDATE,
        },
      ]}
      onResolve={handleResolve}
      onReject={handleReject}
      modalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
      }}
    >
      <Stack>
        {sendLoading && <div style={{
            height: '100%',
            position: 'absolute',
            left: '0',
            top: '0',
            width: '100%',
            overflow: 'hidden',
            zIndex: 100,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <CircularProgress size={34} />
          </div>}
        <FloatingCircularProgress open={sendLoading} />
        {nonAgencyValidNamesList.length > 0 && (
          <ToolTip title={<>{nonAgencyValidNamesList}</>} placement="bottom-start">
              <div>
                <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                  {nonAgencyValidNamesList.length} {' '}
                  {formatPluralString(
                    nonAgencyValidNamesList.length,
                    COMMON_CANDIDATE,
                    COMMON_CANDIDATES,
                  )}
                  <InfoIcon sx={{ marginLeft: '5px' }} />
                </Typography>
              </div>
            </ToolTip>
        )}
        {agencyValidNamesList.length > 0 && (
          <ToolTip title={<>{agencyValidNamesList}</>} placement="bottom-start">
            <div>
              <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                {agencyValidNamesList.length} {' '}
                {COMMON_AGENCY} {' '}
                {formatPluralString(
                  agencyValidNamesList.length,
                  COMMON_CANDIDATE,
                  COMMON_CANDIDATES,
                )}
                <InfoIcon sx={{ marginLeft: '5px' }} />
              </Typography>
            </div>
          </ToolTip>
        )}
        <FormInputLayout gridLayout="calc(100%)">
          <AutoSelect
            required
            label={COMMON_DISPOSITION}
            value={disposition}
            options={rejectCandidatesData.dispositions}
            onChange={(e, value) => setDisposition(value)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.EId === value.EId}
            error={dispositionError !== ''}
            errorText={dispositionError}
          />
        </FormInputLayout>
        <FormGroup row>
          { rejectCandidatesData.rejectionEmailEnabled && <FormControlLabel control={<Checkbox checked={sendRejectionEmail} onChange={(event) => setSendRejectionEmail(event.target.checked)} />} label={CANDIDATE_SEND_REJECTION_EMAIL} /> }
          <FormControlLabel control={<Checkbox checked={rejectFromAll} onChange={(event) => setRejectFromAll(event.target.checked)} />} label={CANDIDATE_REJECT_APPLICATIONS} />
        </FormGroup>
        {sendRejectionEmail && (
          <>
            {(rejectCandidatesData.isRejectLaterEnabled) && (
              <Stack direction="column">
                <FormGroup>
                  <FormControlLabel label={CANDIDATES_REJECT_LATER} control={<Checkbox checked={delayRejection} onChange={(event) => {
                    setDelayRejection(event.target.checked)
                    if (!event.target.checked) {
                      setDaysAfter(DEFAULT_DAYS_AFTER)
                    }
                  }} />} />
                </FormGroup>
                <TextField
                  required
                  label={COMMON_DAYS}
                  fullWidth
                  type="number"
                  margin="normal"
                  value={daysAfter}
                  onChange={(event) => {
                    const number = parseInt(event.target.value)

                    if (number < MINIMUM_DAYS_AFTER || number > MAXIMUM_DAYS_AFTER) {
                      return
                    }

                    setDaysAfter(number)
                  }}
                  onBlur={() => {
                    if (!daysAfter) {
                      setDaysAfter(DEFAULT_DAYS_AFTER)
                    }
                  }}
                  error={daysAfterError !== ''}
                  errorText={daysAfterError}
                  disabled={!delayRejection}
                />
                { delayRejection && <Typography>{formattedDate}</Typography> }
              </Stack>
            )}
            <Divider sx={{ marginTop: '16px', marginBottom: '16px' }} />
            <ToolTip title={CANDIDATES_REJECT_TOOLTIP} placement="bottom-start">
              <div>
                <Typography sx={{ display: 'flex', alignItems: 'center' }}>{COMMON_FROM}: {SEND_ANONYMOUSLY} <HelpIcon sx={{ marginLeft: '5px' }} /></Typography>
              </div>
            </ToolTip>
            <Typography>
             {COMMON_TO}: { toText }
            </Typography>
            {nonValidNonAgencyApplications.length > 0 && (
              <Typography className="warning">
                {formatPluralString(
                  nonValidNonAgencyApplications.length,
                  CANDIDATES_MISSING_EMAIL_1,
                  CANDIDATES_MISSING_EMAIL_MANY,
                  '{0}'
                )}
              </Typography>
            )}
            {nonValidAgencyApplications.length > 0 && (
              <Typography className="warning">
                {formatPluralString(
                  nonValidAgencyApplications.length,
                  AGENCY_CANDIDATES_MISSING_EMAIL_1,
                  AGENCY_CANDIDATES_MISSING_EMAIL_MANY,
                  '{0}'
                )}
              </Typography>
            )}
            <FormInputLayout gridLayout="calc(100%)">
            </FormInputLayout>
            <RadioGroup row value={templateType} onChange={(e, value) => setTemplateType(value)}>
              <FormControlLabel value="company" label={PERSONAL_EMAIL_TEMPLATES_COMPANY_TEMPLATES} control={<Radio />} />
              <FormControlLabel value="personal" label={PERSONAL_EMAIL_TEMPLATES_MY_TEMPLATES} control={<Radio />} />
            </RadioGroup>
            <FormInputLayout gridLayout="calc(100%)">
                {templateType === 'company' && (
                  <AutoSelect
                    label={COMMON_TEMPLATE}
                    value={templateChosen}
                    options={rejectCandidatesData.templates}
                    onChange={(e, value) => setTemplateChosen(value)}
                    getOptionLabel={option => option.title}
                    isOptionEqualToValue={(option, value) => option.EId === value.EId}
                  />
                )}
                {templateType === 'personal' && (
                    <UserEmailTemplatesInput
                      // @ts-ignore
                      value={templateChosen}
                      onChange={(event, newTemplate: any) => {
                        setTemplateChosen(newTemplate)
                      }}
                    />
                  )}
            </FormInputLayout>
            <FormInputLayout gridLayout="calc(100%)">
              <TextField
                required
                label={COMMON_SUBJECT}
                fullWidth
                margin="normal"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                errorText={subjectError}
                error={subjectError !== ''}
              />
            </FormInputLayout>
            <HTMLEditor
                ref={htmlEditorRef}
                variant="standard"
                enterMode="br"
                mergeFields={{}}
                disabled={false}
                onLoad={handleHTMLEditorLoad}
            />
            { bodyError && <Typography sx={{ color: 'red' }}>{JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD}</Typography> }
          </>
        )}
      </Stack>
    </ContentModal>
  );
};

export default RejectCandidateModal;

function useValidatedCandidatesList() {
  const context = useContext(CandidateListContext);

  if (context === null) {
    throw 'This hook must be used within a <CandidateActionModal /> child'
  }

  return useMemo(() => {
    if (context.validatedCandidateList && context.validatedCandidateList.validApplications) {
      return {
        validatedCandidates: context.validatedCandidateList.validApplications,
        validatedCandidateIds: context.validatedCandidateList.validApplicationIds,
      }
    }

    return {
      validatedCandidates: [],
      validatedCandidateIds: [],
    }
  }, [
    context,
    context.validatedCandidateList,
    context.validatedCandidateList.validApplication,
  ])
}

function getToText(candidates: Array<any>, agencyCandidates: Array<any>, locales: {
  agencyCopied: string
  agencyCandidates?: string
  and: string
  candidates: string
}): string {
  let candidateText = ''
  if (candidates.length === 1 && agencyCandidates.length === 0) {
    candidateText = candidates[0].candidate.fullName
  } else if (candidates.length > 0) {
    candidateText = `${candidates.length} ${locales.candidates}`
  }

  let agencyText = ''
  if (agencyCandidates.length === 1 && candidates.length === 0) {
    agencyText = agencyCandidates[0].candidate.fullName
  } else if (agencyCandidates.length > 0) {
    agencyText = `${agencyCandidates.length} ${locales.agencyCandidates} (${locales.agencyCopied})`
  }

  if (candidateText && agencyText) {
    return `${candidateText} ${locales.and} ${agencyText}`
  } else if (candidateText) {
    return `${candidateText}`
  } else if (agencyText) {
    return `${agencyText}`
  }
}
